import Constants from '../Constants.js';
import EnumReducer from './EnumReducer.js';
const {
  getStatus,
  getError,
  getEnum
} = EnumReducer;
class Enum {
  constructor(projectId, type) {
    this.projectId = projectId;
    this.type = type;
  }

  /**
   * @method createAction A creator for actions
   * @memberof Enum
   * @param {string} method The name of the method being created (e.g. 'update')
   * @param {string} status The status of the action (e.g. 'updating')
   * @param {function} func The async function that executes the action
   */
  createAction(method, status, func) {
    return (dispatch, getState, opts) => {
      const projectId = this.projectId || opts?.api?.projectId;
      dispatch({
        type: Constants.ENUM_STATUS,
        enumType: this.type,
        projectId,
        status
      });
      return func(dispatch, getState, opts, projectId).catch(error => {
        dispatch({
          type: Constants.ENUM_STATUS,
          projectId,
          enumType: this.type,
          status: `${method}Error`,
          error
        });
        return Promise.reject(error);
      });
    };
  }

  /**
   * @method createCall Creates linked api call and redux update action
   * @memberof Enum
   * @param {string} method The api.project( projectId ).enum( type ) method to be called
   * @param {string} type Action type to link to redux
   * @param {any[]} args to pass to the above method
   */
  createCall(method, type, args = []) {
    return async (dispatch, getState, {
      api
    }, projectId = this.projectId) => {
      const res = await api.project(projectId).enum(this.type)[method](...args);
      dispatch({
        type,
        projectId,
        enumType: this.type,
        data: res
      });
      if (type !== Constants.ENUM_REMOVE) {
        dispatch({
          type: Constants.ENUM_STATUS,
          enumType: this.type,
          projectId,
          status: null
        });
      }
      return res;
    };
  }

  /**
   * @method get An action to get the Enum
   * @memberof Enum
   * @param {object} opts Options for the `enum().get( opts )` api call
   * @returns {promise}
   */
  get(opts) {
    return this.createAction('load', 'loading', this.createCall('get', Constants.ENUM_GET, [opts]));
  }

  /**
   * @method update An action to update the Enum
   * @memberof Enum
   * @param {object} data The patch object for the Enum
   * @returns {promise}
   */
  update(data) {
    return this.createAction('update', 'updating', this.createCall('update', Constants.ENUM_UPDATE, [data]));
  }

  /**
   * @method addValue An action to add a value to the Enum
   * @memberof Enum
   * @param {object} value The key of the value to add
   * @param {object} [data] Additional data associated with the value
   * @param {object} [data.metadata]
   * @param {string} [data.name] Human readable name of the value
   * @param {string} [data.description] Description of the value
   * @param {boolean} [data.locked] Whether the value is locked
   * @returns {promise}
   */
  addValue(value, data) {
    return this.createAction('update', 'updating', this.createCall('addValue', Constants.ENUM_ADD_VALUE, [value, data]));
  }

  /**
   * @method updateValue An action to update the properties of specified Enum value
   * @memberof Enum
   * @param {object} value The key of the value to update
   * @param {object} [data] Additional data associated with the value
   * @param {object} [data.metadata]
   * @param {string} [data.name] Human readable name of the value
   * @param {string} [data.description] Description of the value
   * @param {boolean} [data.locked] Whether the value is locked
   * @returns {promise}
   */
  updateValue(value, data) {
    return this.createAction('update', 'updating', this.createCall('updateValue', Constants.ENUM_UPDATE_VALUE, [value, data]));
  }

  /**
   * @method removeValue An action to update the properties of specified Enum value
   * @memberof Enum
   * @param {object} value The key of the value to update
   * @returns {promise}
   */
  removeValue(value) {
    return this.createAction('delete', 'deleting', this.createCall('removeValue', Constants.ENUM_REMOVE_VALUE, [value]));
  }

  /**
   * @method values An action to list all the enum values
   * @param {object} opts Options for the `enum( type ).values( opts )` api call
   */
  values(opts) {
    return this.createAction('list', 'loading', this.createCall('values', Constants.ENUM_VALUES, [opts]));
  }

  /**
   * @method remove An action to delete the enum
   * @memberof Enum
   */
  remove() {
    return this.createAction('delete', 'deleting', this.createCall('remove', Constants.ENUM_REMOVE));
  }

  /**
   * @method getStatus Get the status of the Enum actions from Redux state
   * @memberof Enum
   * @param {string} [type] enum type to check
   * @returns {promise}
   */
  getStatus(state) {
    return getStatus(state, this.projectId, this.type);
  }

  /**
   * @method getError Get the current error for the Enum actions from Redux state
   * @memberof Enum
   * @param {string} [type] enum type to check
   * @returns {promise}
   */
  getError(state) {
    return getError(state, this.projectId, this.type);
  }
  getEnum(state) {
    return getEnum(state, this.projectId, this.type);
  }
}
export default Enum;